import { UTBox, UTButton, UTTypography, ColorVariables } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { UserProfile, UserRoles } from '../../../UserManagement/Types/UserManagement.types';
import { ManagePermissionsModal } from '../../Modals/ManagePermissionsModal';
import * as S from './UserOverviewContent.styles';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { ChildContent } from '@/Templates/Main/ChildContent/ChildContent';

interface UserOverviewContentProps {
  profile: UserProfile;
  roles: UserRoles | null;
  onBack?: () => void;
  onRefresh?: () => void;
}

const UserOverviewContent = ({ profile, roles, onBack, onRefresh }: UserOverviewContentProps) => {
  const { pages } = usePrivileges();
  const [managePermissionsOpen, setManagePermissionsOpen] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const isLocked = profile.lockedUntil && new Date(profile.lockedUntil) > new Date();

  return (
    <>
      <ChildContent
        topTitle={{
          title: `User: ${profile.name}`,
          subtitle: profile.email,
        }}
        topAddon={
          <UTButton
            variant='tertiary'
            text='Back'
            onClick={onBack}
            icon='Arrows.ArrowBendDoubleUpLeft'
          />
        }
      >
        <UTBox flexDirection='column' gap='24px' width='100%' padding='20px'>
          {/* Profile Information Card */}
          <UTBox
            className={S.card}
            flexDirection='column'
            gap='16px'
            padding='20px'
            borderRadius='8px'
            backgroundColor='#fff'
            border='1px solid rgba(0,0,0,0.1)'
          >
            <UTBox flexDirection='row' justifyContent='space-between' alignItems='center'>
              <UTTypography fontSize='16px' fontWeight='600'>
                Profile Information
              </UTTypography>
            </UTBox>

            <UTBox
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Name:
              </UTTypography>
              <UTTypography fontSize='14px' fontWeight='600'>
                {profile.name}
              </UTTypography>
            </UTBox>

            <UTBox
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Email:
              </UTTypography>
              <UTTypography fontSize='14px' fontWeight='600'>
                {profile.email}
              </UTTypography>
            </UTBox>

            <UTBox
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Created:
              </UTTypography>
              <UTTypography fontSize='14px' fontWeight='600'>
                {formatDate(profile.createdAt)}
              </UTTypography>
            </UTBox>

            <UTBox flexDirection='row' justifyContent='space-between' alignItems='center' py='12px'>
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Last Updated:
              </UTTypography>
              <UTTypography fontSize='14px' fontWeight='600'>
                {formatDate(profile.updatedAt)}
              </UTTypography>
            </UTBox>
          </UTBox>

          {/* Roles & Permissions Card */}
          <UTBox
            className={S.card}
            flexDirection='column'
            gap='16px'
            padding='20px'
            borderRadius='8px'
            backgroundColor='#fff'
            border='1px solid rgba(0,0,0,0.1)'
          >
            <UTBox flexDirection='row' justifyContent='space-between' alignItems='center'>
              <UTTypography fontSize='16px' fontWeight='600'>
                Roles & Permissions
              </UTTypography>
              {pages.SETTINGS.USERS.MANAGE_PERMISSIONS && (
                <UTButton
                  variant='tertiary'
                  text='Manage'
                  size='small'
                  onClick={() => setManagePermissionsOpen(true)}
                  icon='Settings.Gear'
                />
              )}
            </UTBox>

            {/* Roles */}
            <UTBox
              flexDirection='column'
              gap='8px'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='13px' fontWeight='600' color='rgba(0,0,0,0.7)'>
                Assigned Roles:
              </UTTypography>
              {roles && roles.roles.length > 0 ? (
                <UTBox flexDirection='row' gap='8px' flexWrap='wrap'>
                  {roles.roles.map((role, idx) => {
                    const roleName = typeof role === 'string' ? role : role.name;
                    const roleKey = typeof role === 'string' ? `${role}-${idx}` : role.id;
                    return (
                      <UTBox
                        key={roleKey}
                        padding='6px 12px'
                        borderRadius='4px'
                        backgroundColor={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND}
                        display='fit-content'
                      >
                        <UTTypography
                          text={roleName}
                          fontSize='12px'
                          fontWeight='600'
                          color='#fff'
                        />
                      </UTBox>
                    );
                  })}
                </UTBox>
              ) : (
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.5)'>
                  No roles assigned
                </UTTypography>
              )}
            </UTBox>

            {/* Direct Permissions */}
            {profile.permissions && profile.permissions.length > 0 && (
              <UTBox flexDirection='column' gap='8px' py='12px'>
                <UTTypography fontSize='13px' fontWeight='600' color='rgba(0,0,0,0.7)'>
                  Direct Permissions ({profile.permissions.length}):
                </UTTypography>
                <UTBox flexDirection='column' gap='6px'>
                  {profile.permissions.map((perm, idx) => (
                    <UTTypography key={idx} fontSize='12px' color='rgba(0,0,0,0.6)'>
                      • {perm}
                    </UTTypography>
                  ))}
                </UTBox>
              </UTBox>
            )}
          </UTBox>

          {/* Security Information Card */}
          <UTBox
            className={S.card}
            flexDirection='column'
            gap='16px'
            padding='20px'
            borderRadius='8px'
            backgroundColor='#fff'
            border='1px solid rgba(0,0,0,0.1)'
          >
            <UTTypography fontSize='16px' fontWeight='600'>
              Security Information
            </UTTypography>

            <UTBox
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Failed Login Attempts:
              </UTTypography>
              <UTBox
                padding='4px 12px'
                borderRadius='4px'
                backgroundColor={
                  profile.failedLoginAttempts > 3
                    ? 'rgba(220, 38, 38, 0.1)'
                    : 'rgba(74, 144, 226, 0.1)'
                }
              >
                <UTTypography
                  fontSize='14px'
                  fontWeight='600'
                  color={
                    profile.failedLoginAttempts > 3
                      ? '#dc2626'
                      : ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND
                  }
                >
                  {profile.failedLoginAttempts}
                </UTTypography>
              </UTBox>
            </UTBox>

            <UTBox
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              py='12px'
              borderBottom='1px solid rgba(0,0,0,0.1)'
            >
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Account Status:
              </UTTypography>
              <UTBox
                padding='4px 12px'
                borderRadius='4px'
                backgroundColor={isLocked ? 'rgba(220, 38, 38, 0.1)' : 'rgba(74, 144, 226, 0.1)'}
              >
                <UTTypography
                  fontSize='14px'
                  fontWeight='600'
                  color={
                    isLocked ? '#dc2626' : ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND
                  }
                >
                  {isLocked ? 'Locked' : 'Active'}
                </UTTypography>
              </UTBox>
            </UTBox>

            {isLocked && profile.lockedUntil && (
              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                alignItems='center'
                py='12px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                  Locked Until:
                </UTTypography>
                <UTTypography fontSize='14px' fontWeight='600' color='#dc2626'>
                  {formatDate(profile.lockedUntil)}
                </UTTypography>
              </UTBox>
            )}

            <UTBox flexDirection='row' justifyContent='space-between' alignItems='center' py='12px'>
              <UTTypography fontSize='14px' fontWeight='500' color='rgba(0,0,0,0.7)'>
                Last Login:
              </UTTypography>
              <UTTypography fontSize='14px' fontWeight='600'>
                {profile.lastLoginAt ? formatDate(profile.lastLoginAt) : 'Never'}
              </UTTypography>
            </UTBox>
          </UTBox>
        </UTBox>
      </ChildContent>

      <ManagePermissionsModal
        open={managePermissionsOpen}
        onClose={() => setManagePermissionsOpen(false)}
        onSuccess={() => {
          setManagePermissionsOpen(false);
          onRefresh?.();
        }}
        userId={profile.id}
      />
    </>
  );
};

export { UserOverviewContent };
